import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { apiJson, ApiError } from '@/lib/api';
import type { AdminUserDetailResponse, SendPushNotificationResponse } from '@/lib/types';

function parseDetail(err: unknown): string {
  if (err instanceof ApiError && err.bodyText) {
    try {
      const j = JSON.parse(err.bodyText) as { detail?: string };
      if (j.detail) return j.detail;
    } catch {
      /* ignore */
    }
    return err.bodyText;
  }
  return err instanceof Error ? err.message : 'Request failed';
}

function AdminUserToolsPage() {
  const { userId } = Route.useParams();
  const id = Number(userId);
  const { getToken } = useAuth();

  const userQuery = useQuery({
    queryKey: ['admin', 'user-detail', id],
    queryFn: () => apiJson<AdminUserDetailResponse>(getToken, `/v1/admin/users/${id}`),
  });

  const detail = userQuery.data;

  const [pushTitle, setPushTitle] = useState('');
  const [pushBody, setPushBody] = useState('');
  const [pushDeeplink, setPushDeeplink] = useState('');
  const [pushBanner, setPushBanner] = useState<{ ok: boolean; text: string } | null>(null);

  const pushMutation = useMutation({
    mutationFn: () =>
      apiJson<SendPushNotificationResponse>(getToken, `/v1/admin/users/${id}/notifications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: pushTitle.trim(),
          body: pushBody.trim(),
          deeplink: pushDeeplink.trim() || null,
        }),
      }),
    onSuccess: (data) => {
      setPushBanner({ ok: data.success, text: data.message });
      if (data.success) {
        setPushTitle('');
        setPushBody('');
        setPushDeeplink('');
      }
    },
    onError: (e) => setPushBanner({ ok: false, text: parseDetail(e) }),
  });

  const canSendPush = pushTitle.trim().length > 0 && pushBody.trim().length > 0;

  if (userQuery.isLoading) {
    return (
      <div className='flex justify-center py-12'>
        <div className='animate-spin h-8 w-8 border-2 border-nova-primary border-t-transparent rounded-full' />
      </div>
    );
  }

  if (userQuery.isError || !detail) {
    return <p className='text-red-600'>Failed to load user.</p>;
  }

  const displayName =
    [detail.first_name, detail.last_name].filter(Boolean).join(' ') || `User #${id}`;

  return (
    <div className='space-y-4'>
      <h2 className='text-lg font-semibold text-nova-foreground'>Tools</h2>

      <section className='bg-nova-card rounded-lg shadow-sm border border-nova-border p-4 space-y-4'>
        <h3 className='text-base font-semibold text-nova-foreground'>Push notification</h3>
        <p className='text-sm text-nova-muted-foreground'>
          Sends a push to <strong>{displayName}</strong> (user #{id}) and creates an in-app
          notification. Uses{' '}
          <code className='text-xs bg-nova-muted px-1 py-0.5 rounded'>
            POST /v1/admin/users/{'{user_id}'}/notifications
          </code>
          .
        </p>

        {pushBanner ? (
          <div
            className={`rounded-lg border px-4 py-3 text-sm ${
              pushBanner.ok
                ? 'bg-green-50 border-green-200 text-green-900'
                : 'bg-red-50 border-red-200 text-red-800'
            }`}
          >
            {pushBanner.text}
          </div>
        ) : null}

        {!detail.has_push_token ? (
          <p className='text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3'>
            This user has no push token registered. They must open the app and allow
            notifications before a push can be delivered.
          </p>
        ) : (
          <div className='space-y-3'>
            <input
              value={pushTitle}
              onChange={(e) => {
                setPushTitle(e.target.value);
                setPushBanner(null);
              }}
              placeholder='Title'
              className='w-full px-3 py-2 border border-nova-input rounded-md text-sm'
            />
            <textarea
              value={pushBody}
              onChange={(e) => {
                setPushBody(e.target.value);
                setPushBanner(null);
              }}
              placeholder='Body'
              rows={3}
              className='w-full px-3 py-2 border border-nova-input rounded-md text-sm'
            />
            <input
              value={pushDeeplink}
              onChange={(e) => {
                setPushDeeplink(e.target.value);
                setPushBanner(null);
              }}
              placeholder='Deeplink (optional)'
              className='w-full px-3 py-2 border border-nova-input rounded-md text-sm'
            />
            <button
              type='button'
              disabled={!canSendPush || pushMutation.isPending}
              onClick={() => pushMutation.mutate()}
              className='px-4 py-2 text-sm font-semibold bg-nova-primary text-white rounded-md disabled:opacity-50'
            >
              {pushMutation.isPending ? 'Sending…' : 'Send push'}
            </button>
          </div>
        )}
      </section>
    </div>
  );
}

export const Route = createFileRoute('/admin/users/$userId/tools')({
  validateSearch: (search: Record<string, unknown>) => ({
    hid: typeof search.hid === 'string' ? search.hid : undefined,
  }),
  component: AdminUserToolsPage,
});
